<template>
  <base-slide-over ref="filterSlideover" title="Filter Report" size="lg" style="z-index: 10000">
    <base-form ref="form" class="tw-py-2.5 tw-px-4 sm:tw-py-4 sm:tw-px-6" @submit.prevent>
      <div v-show="showDateFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-2.5 tw-mb-2.5">{{ dateFilterLabel }}</div>
        <base-button-toggle-group ref="dateToggle" size="xs" :options="dateShortcuts" @selected="dateShortcutSelected" />
        <div v-show="showDateFields" class="tw-flex tw-mt-2.5 tw-gap-4">
          <base-date-picker label="Start Date" v-model="startDate" :typeable="true" :inline="false" />
          <base-date-picker label="End Date" v-model="endDate" :typeable="true" :inline="false" />
        </div>
      </div>
      <div v-show="showLocationsFilter">
        <div class="tw-text-normal tw-font-normal tw-mb-2.5 tw-mt-6">Locations</div>
        <base-advanced-select label="Locations" placeholder="Select one or more locations" :options="groupedLocationOptions" v-model="locationsFilter" label-key="name" multiple />
      </div>
      <div v-show="showPrescriptionStatusFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-6 tw-mb-2.5">Status</div>
        <div class="tw-grid tw-grid-rows-3 tw-grid-flow-col tw-gap-4">
          <div v-for="status in prescriptionStatusOptions" :key="status.value">
            <base-checkbox :label="status.label" :value="status.value" v-model="prescriptionStatusFilter" />
          </div>
        </div>
      </div>
      <div v-show="showProgramGroupsFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-6 tw-mb-2.5">Program Groups</div>
        <div class="tw-grid tw-grid-rows-3 tw-grid-flow-col tw-gap-4">
          <div v-for="group in programGroups" :key="group">
            <base-checkbox :label="group" :value="group" v-model="programGroupsFilter" />
          </div>
        </div>
      </div>
      <div v-show="showProgramsFilter">
        <div class="tw-text-normal tw-font-normal tw-mb-2.5 tw-mt-6">Programs</div>
        <base-advanced-select label="Programs" placeholder="Select one or more programs" :options="groupedProgramOptions" v-model="programsFilter" label-key="name" multiple />
      </div>

      <div v-show="showPlanStatusFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-6 tw-mb-2.5">Status</div>
        <div class="tw-grid tw-grid-rows-3 tw-grid-flow-col tw-gap-4">
          <div v-for="status in planStatusOptions" :key="status.value">
            <base-checkbox :label="status.label" :value="status.value" v-model="planStatusFilter" />
          </div>
        </div>
      </div>
      <div v-show="showPlanGroupsFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-6 tw-mb-2.5">Plan Groups</div>
        <base-advanced-select label="Plan Groups" placeholder="Select one or more plan groups" :options="planGroups" v-model="planGroupsFilter" label-key="group" multiple />
      </div>
      <div v-show="showPlansFilter">
        <div class="tw-text-normal tw-font-normal tw-mb-2.5 tw-mt-6">Plans</div>
        <base-advanced-select label="Plans" placeholder="Select one or more plans" :options="groupedPlanOptions" v-model="plansFilter" label-key="name" multiple />
      </div>
      <div v-show="showPatientStateFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mb-2.5 tw-mt-6">{{ capitalize(organization.customerLabelSingular) }} State</div>
        <base-advanced-select label="State" placeholder="Select one or more states" :options="statesOptions" v-model="statesFilter" label-key="name" multiple />
      </div>
      <div v-show="showSelfOnBoardingFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mb-2.5 tw-mt-6">{{ capitalize(organization.customerLabelSingular) }} Self-Onboarding</div>
        <div class="tw-grid tw-grid-rows-3 tw-grid-flow-col tw-gap-4">
          <base-checkbox :label="'Patient Self-Onboarding'" :value="'self-onboarding'" v-model="selfOnBoardingFilter" />
        </div>
      </div>
      <div v-show="showIntegrityStatusFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-6 tw-mb-2.5">Integrity Status</div>
        <div class="tw-grid tw-grid-rows-3 tw-grid-flow-col tw-gap-4">
          <div v-for="status in integrityStatusOptions" :key="status.value">
            <base-checkbox :label="status.label" :value="status.value" v-model="integrityStatusFilter" />
          </div>
        </div>
      </div>
      <div v-show="showFormulaFilter === true">
        <div class="tw-text-normal tw-font-normal tw-mt-6 tw-mb-2.5">Formula</div>
        <base-advanced-select label="Formulas" placeholder="Select one or more formulas" :options="formulaOptions" v-model="formulasFilter" label-key="label" multiple />
      </div>
    </base-form>
    <template #fixed-bottom>
      <div class="tw-p-3 tw-flex tw-justify-between">
        <base-button type="flat" color="red" class="tw-w-auto tw-mr-2" @click="clearFilters">
          Clear All
        </base-button>
        <div>
          <base-button type="flat" class="tw-w-auto tw-mr-2" @click="$refs.filterSlideover.hide()">
            Cancel
          </base-button>
          <base-button class="tw-w-auto" @click="applyFilter">
            Apply
          </base-button>
        </div>
      </div>
    </template>
  </base-slide-over>
</template>

<script>
import { mapGetters } from "vuex";
import { STATE_OPTIONS, PRESCRIPTION_STATUS_OPTIONS, PLAN_STATUS_OPTIONS } from "@/constants/constants";
import { orderBy, capitalize } from "lodash";

export default {
  name: "ReportFilterSlideover",
  props: {
    businessId: { type: String },
    showDateFilter: { type: Boolean, default: true },
    dateFilterLabel: { type: String, default: "Date" },
    showLocationsFilter: { type: Boolean, default: true },
    showProgramsFilter: { type: Boolean, default: true },
    showProgramGroupsFilter: { type: Boolean, default: true },
    showPrescriptionStatusFilter: { type: Boolean, default: true },
    showPlansFilter: { type: Boolean, default: true },
    showPlanGroupsFilter: { type: Boolean, default: true },
    showPlanStatusFilter: { type: Boolean, default: true },
    showPatientStateFilter: { type: Boolean, default: false },
    showSelfOnBoardingFilter: { type: Boolean, default: false },
    showIntegrityStatusFilter: { type: Boolean, default: false },
    showFormulaFilter: { type: Boolean, default: false },
  },
  components: {},
  data() {
    return {
      itemTypes: [],
      locationsFilter: [],
      startDate: null,
      endDate: null,
      showDateFields: false,
      plansFilter: [],
      planGroupsFilter: [],
      planStatusFilter: [],
      programsFilter: [],
      programGroupsFilter: [],
      prescriptionStatusFilter: [],
      statesFilter: [],
      statesOptions: STATE_OPTIONS,
      prescriptionStatusOptions: PRESCRIPTION_STATUS_OPTIONS,
      planStatusOptions: PLAN_STATUS_OPTIONS,
      integrityStatusFilter: [],
      integrityStatusOptions: [
        { label: "Good", value: "Good" },
        { label: "Bad", value: "Bad" },
        { label: "Investigate", value: "Investigate" },
        { label: "Neutral", value: "Neutral" },
        { label: "Empty", value: "Empty" },
      ],
      formulasFilter: [],
      formulaOptions: [
        { label: "0 / 0 / 0 = Empty, Generally Good", value: "0 / 0 / 0" },
        { label: "1 / 1 / 1 = Good", value: "1 / 1 / 1" },
        { label: "1 / 1 / 0 = Bad", value: "1 / 1 / 0" },
        { label: "FP / 1 / X  = Good", value: "FP / 1 / X" },
        { label: "1 / 0 / 0 = Bad", value: "1 / 0 / 0" },
        { label: "Active / 0 / 0 = Bad", value: "Active / 0 / 0" },
        { label: "FP / 0 / 0 = Neutral, Rare", value: "FP / 0 / 0" },
        { label: "0 / 0 / 1 = Investigate, Generally Good", value: "0 / 0 / 1" },
        { label: "0 / 0 / OPC = Good", value: "0 / 0 / OPC" },
        { label: "0 / 0 / Deprecated Activity = Bad, Rare", value: "0 / 0 / Deprecated Activity" },
        { label: "1 / 0 / 1 = Investigate, Bad", value: "1 / 0 / 1" },
        { label: "0 / 1 / 1 = Investigate, Generally Good", value: "0 / 1 / 1" },
        { label: "0 / Accepted / 1 = Good if VIP", value: "0 / Accepted / 1" },
        { label: "0 / DE / 1 = Good", value: "0 / DE / 1" },
        { label: "0 / Partial / 1 = Good", value: "0 / Partial / 1" },
        { label: "0 / 1 / 0 = Generally Good", value: "0 / 1 / 0" },
        { label: "0 / New / 0 = Good", value: "0 / New / 0" },
        { label: "0 / Declined / 0 = Neutral, Rare", value: "0 / Declined / 0" },
        { label: "0 / Partial / 0 = Good", value: "0 / Partial / 0" },
        { label: "0 / DE / 0  = Good", value: "0 / DE / 0" },
        { label: "Cancelled / X / X  = Investigate, Generally Good", value: "Cancelled / X / X" },
      ],
      dateShortcuts: [
        { label: "Today", value: "today" },
        { label: "Yesterday", value: "yesterday" },
        { label: "This Week", value: "week" },
        { label: "This month", value: "month" },
        { label: "YTD", value: "ytd" },
        { label: "Custom", value: "custom" },
      ],
      plans: [],
      planGroups: [],
      programs: [],
      programGroups: [],
      selfOnBoardingFilter: [],
    };
  },
  computed: {
    ...mapGetters("location", ["locations"]),
    ...mapGetters("organization", ["organization"]),
    groupedLocationOptions() {
      const options = [];
      const groups = [...new Set(this.locations.map((item) => item.state))];

      groups.forEach((g) => {
        options.push({
          name: STATE_OPTIONS.find((x) => x.value === g).name,
          businessLocationId: null,
          isHeader: true,
        });

        options.push(...orderBy(this.locations.filter((p) => p.state === g), ["name"]));
      });

      return options;
    },
    groupedProgramOptions() {
      const options = [];

      this.programGroups.forEach((g) => {
        options.push({
          name: g,
          businessProgramId: null,
          isHeader: true,
        });

        options.push(...orderBy(this.programs.filter((p) => p.programGroup === g), ["name"]));
      });

      return options;
    },
    groupedPlanOptions() {
      const options = [];

      this.planGroups.forEach((g) => {
        options.push({
          name: g,
          businessPlanId: null,
          isHeader: true,
        });

        options.push(...orderBy(this.plans.filter((p) => p.planGroup === g), ["name"]));
      });

      return options;
    },
  },
  created() {
    if (this.showPlansFilter || this.showPlanGroupsFilter) this.getPlans();
    if (this.showProgramsFilter || this.showProgramGroupsFilter) this.getPrograms();
  },
  methods: {
    capitalize,
    show() {
      this.$refs.filterSlideover.show();
    },
    hide() {
      this.$refs.filterSlideover.hide();
    },
    clearFilters() {
      this.reset();
      this.applyFilter();
    },
    reset() {
      this.locationsFilter = [];
      this.startDate = null;
      this.endDate = null;
      this.programGroupsFilter = [];
      this.programsFilter = [];
      this.prescriptionStatusFilter = [];
      this.planStatusFilter = [];
      this.planGroupsFilter = [];
      this.plansFilter = [];
      this.statesFilter = [];
      this.showDateFields = false;
      this.selfOnBoardingFilter = [];
      this.integrityStatusFilter = [];
      this.formulasFilter = [];
      this.$refs.dateToggle.clearSelection();
    },
    dateShortcutSelected(value) {
      switch (value) {
        case "today":
          this.startDate = new Date();
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.showDateFields = false;
          break;
        case "yesterday":
          this.startDate = new Date();
          this.startDate.setDate(this.startDate.getDate() - 1);
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.endDate.setDate(this.endDate.getDate() - 1);
          this.endDate.setHours(23, 59, 59, 999);
          this.showDateFields = false;
          break;
        case "week":
          this.startDate = new Date();
          this.startDate.setDate(this.startDate.getDate() - this.startDate.getDay() + 1);
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.showDateFields = false;
          break;
        case "month":
          this.startDate = new Date();
          this.startDate.setDate(1);
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.showDateFields = false;
          break;
        case "ytd":
          this.startDate = new Date();
          this.startDate.setDate(1);
          this.startDate.setMonth(0);
          this.startDate.setHours(0, 0, 0, 0);
          this.endDate = new Date();
          this.showDateFields = false;
          break;
        case "custom":
          this.startDate = null;
          this.endDate = null;
          this.showDateFields = true;
          break;
      }
    },
    applyFilter() {
      if (this.startDate) this.startDate.setHours(0, 0, 0, 0);
      if (this.endDate) this.endDate.setHours(23, 59, 59, 999);

      this.$emit("filter-updated", {
        startDate: this.startDate,
        endDate: this.endDate,
        locationsFilter: this.locationsFilter,
        prescriptionStatusFilter: this.prescriptionStatusFilter,
        programsFilter: this.programsFilter,
        programGroupsFilter: this.programGroupsFilter,
        planStatusFilter: this.planStatusFilter,
        plansFilter: this.plansFilter,
        planGroupsFilter: this.planGroupsFilter,
        statesFilter: this.statesFilter,
        selfOnBoardingFilter: this.selfOnBoardingFilter,
        integrityStatusFilter: this.integrityStatusFilter,
        formulasFilter: this.formulasFilter,
      });

      this.hide();
    },
    getPlans() {
      this.$axios.get(`/businesses/${this.businessId}/plans/list`).then((res) => {
        this.plans = res.data;
        this.planGroups = [...new Set(res.data.map((x) => x.planGroup))];
      });
    },
    getPrograms() {
      this.$axios.get(`/businesses/${this.businessId}/programs/list?includeDeleted=true`).then((res) => {
        this.programs = res.data;
        this.programGroups = [...new Set(res.data.map((x) => x.programGroup))];
      });
    },
  },
};
</script>
